import type {WsRecipient} from "@/ws/WsRecipient.ts";

export interface SocketData {
	recipient: string | string[];
	sender: string;
	message: any;
}


class UniversalSocket {

	private socket: WebSocket;

	/** Map of known recipients */
	private recipients:Map<string, WsRecipient>;

	/** Outbox of messages to be sent */
	private afterConnectMessages:SocketData[];

	private openHandler: (e:Event) => void;
	private closeHandler: (e:Event) => void;
	private messageHandler: (e:Event) => void;
	private errorHandler: (e:Event) => void;

	constructor() {
		this.afterConnectMessages = [];
		this.recipients = new Map();

		this.openHandler = this.socketOpen.bind(this);
		this.closeHandler = this.socketClose.bind(this);
		this.messageHandler = this.socketMessage.bind(this);
		this.errorHandler = this.socketError.bind(this);

		this.connect();
	}

	connect() {
		let url = new URL('uws', window.location.href);	// "uws" is the name of the server socket impl.
		url.protocol = url.protocol.replace('http', 'ws');

		if (this.socket != null) {
			this.disconnect(this.socket);
		}

		this.socket = new WebSocket(url.href);

		this.socket.addEventListener("open", this.openHandler);
		this.socket.addEventListener("close", this.closeHandler);
		this.socket.addEventListener("message", this.messageHandler);
		this.socket.addEventListener("error", this.errorHandler);
	}

	disconnect(old: WebSocket) {
		old.removeEventListener("open", this.openHandler);
		old.removeEventListener("close", this.closeHandler);
		old.removeEventListener("message", this.messageHandler);
		old.removeEventListener("error", this.errorHandler);
	}

	healthy() {
		return this.socket != null && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING);
	}

	/**
	 * Delivers message from client (sender) to the server (recipient).
	 */
	send(sender:string, recipient:string | string[], value:any) {
		const msg:SocketData = {
			sender: sender,
			recipient : recipient,
			message : value
		};

		if (!this.healthy()) {
			// try to reconnect if closed
			this.connect();
		}

		if (this.socket.readyState === WebSocket.OPEN) {
			this.socket.send(JSON.stringify(msg));	// send now
		} else if (this.socket.readyState === WebSocket.CONNECTING) {
			this.afterConnectMessages.push(msg);	// send as soon as we connect
		} else {
			console.error("Cannot send message to the server - socket not ready [" + this.socket.readyState + "]");
		}
	}

	socketOpen(e:Event) {
		for (let msg of this.afterConnectMessages) {
			this.socket.send(JSON.stringify(msg));
		}
		this.afterConnectMessages.length = 0;
	}

	socketClose(e:CloseEvent) {
		this.disconnect(this.socket);
		this.socket = null;
	}

	socketError(e:Event) {
		console.error("WebSocket error " + e);
	}

	socketMessage(event:MessageEvent) {
		const data = JSON.parse(event.data) as SocketData;
		this.deliverMessage(data);
	}

	deliverMessage(data:SocketData) {
		if (typeof data.recipient === "string") {
			if (data.recipient === "ALL") {
				// Send to all recipients
				this.recipients.forEach((address, instance) => {
					this.deliverMessageTo(data.sender, instance, data.message);
				});
			} else {
				// Send to one recipient
				const inst = this.recipients.get(data.recipient);
				if (inst) this.deliverMessageTo(data.sender, inst, data.message);
			}
		} else if (Array.isArray(data.recipient)) {
			// Send to multiple recipients
			data.recipient.forEach((address) => {
				const inst = this.recipients.get(address);
				if (inst) this.deliverMessageTo(data.sender, inst, data.message);
			});
		}
	}

	/**
	 * @param {string} sender
	 * @param {WsRecipient} toInstance
	 * @param {*} msg
	 */
	deliverMessageTo(sender, toInstance, msg) {
		try {
			toInstance.onMessage(sender, msg);
		} catch (e) {
			console.error("Error processing socket message: " + e);
		}
	}

	/**
	 * Register a new recipient of socket data
	 * @param address - unique component address
	 * @param instance
	 */
	register(address:string, instance:WsRecipient) {
		this.recipients.set(address, instance);
	}

	unregister(address) {
		this.recipients.delete(address);
	}
}

const crmWS = new UniversalSocket();
export default crmWS;
window.crmWS = crmWS;	// for non-modular scripts